/**
 * RestoreConfirmModal (Spec 201) — commit/cancel gate before a restore launches.
 *
 * Shows where the data lands (a NEW <source>_restored_<ts> item — restores are
 * non-destructive, so the existing item is never overwritten) and exactly which tables
 * will be restored, in a scrollable grid that copes with large table lists.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React from 'react';
import { RotateCcw, Database, Warehouse } from 'lucide-react';
import { Modal, Button } from '@/components/ui';
import { formatEastern } from '@/utils/dateFormatter';
import type { BackupEntityType } from '../../api/restore';

export interface RestoreConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  entityType: BackupEntityType;
  sourceName: string;
  pointTime: string;
  destinationWorkspace: string;
  /** null = restore the whole backup (all tables); otherwise the selected subset. */
  tables: string[] | null;
  /** When set, restore is renamed copies INTO this existing lakehouse (not a new item). */
  renamedInto?: string;
}

export const RestoreConfirmModal: React.FC<RestoreConfirmModalProps> = ({
  open,
  onCancel,
  onConfirm,
  entityType,
  sourceName,
  pointTime,
  destinationWorkspace,
  tables,
  renamedInto,
}) => {
  const Icon = entityType === 'warehouse' ? Warehouse : Database;
  const count = tables?.length ?? null;
  const actionLabel = count != null ? `Restore ${count} table${count === 1 ? '' : 's'}` : 'Restore';

  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      size="lg"
      title={count != null ? `Restore ${count} selected table${count === 1 ? '' : 's'}` : 'Restore backup'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={onConfirm}>
            <RotateCcw className="h-4 w-4" /> {actionLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* non-destructive notice — answers "the table already exists, what happens?" */}
        <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
          {renamedInto ? (
            <p className="text-sm text-[var(--color-text-secondary)]">
              Restores these tables as <strong className="text-[var(--color-text-primary)]">renamed copies</strong>{' '}
              (<code className="rounded bg-[var(--color-bg-card)] px-1">&lt;table&gt;_restored_&lt;period&gt;</code>) into the existing {entityType}{' '}
              <strong className="text-[var(--color-text-primary)]">{renamedInto}</strong>. The live tables there are{' '}
              <strong className="text-[var(--color-text-primary)]">never overwritten</strong> — the copies have different names, for side-by-side compare.
            </p>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">
              This creates a <strong className="text-[var(--color-text-primary)]">new {entityType}</strong>{' '}
              <code className="rounded bg-[var(--color-bg-card)] px-1">{sourceName}_restored_&lt;timestamp&gt;</code>. Your existing{' '}
              <strong className="text-[var(--color-text-primary)]">{sourceName}</strong> is <strong className="text-[var(--color-text-primary)]">not modified</strong> — nothing is overwritten, even if a table of the same name already exists.
            </p>
          )}
        </div>

        {/* summary */}
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-[var(--color-text-secondary)]">Point in time</dt>
            <dd className="text-sm text-[var(--color-text-primary)]">{formatEastern(pointTime)}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-text-secondary)]">Destination workspace</dt>
            <dd className="text-sm text-[var(--color-text-primary)]">{destinationWorkspace}</dd>
          </div>
        </dl>

        {/* tables */}
        <div>
          <p className="mb-1.5 text-sm font-medium text-[var(--color-text-primary)]">
            {count != null ? `Tables to restore (${count})` : 'All tables in this backup'}
          </p>
          {tables && tables.length > 0 && (
            <div className="max-h-[42vh] overflow-y-auto rounded-[var(--radius-md)] border border-[var(--color-border)]">
              <div className="grid grid-cols-1 gap-px bg-[var(--color-border)] sm:grid-cols-2 lg:grid-cols-3">
                {tables.map((t) => (
                  <div key={t} className="truncate bg-[var(--color-bg-card)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]" title={t}>
                    {t}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RestoreConfirmModal;
